// @ts-nocheck

import cn from 'classnames';
import PropTypes from 'prop-types';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useIntl } from 'react-intl';
import { useSelector } from 'react-redux';
import { ButtonUI, Selector } from 'sonata-react-components';
import { checkUserResourcePermissions } from 'sonata-static-storage/lib/helpers/user.helpers';

import { getDefaultMessageSelectorKpis, getMetricsKeys } from '@/helpers/reporting.helpers';
import { getImpressionsKpiChartSelector } from '@/store/selectors/reporting.selectors';
import './reportingMetricsAndKpisSelector.scss';

import { getUserRoles } from '@/store/selectors/resources.selectors';
import { getCurrentUser } from '@/store/selectors/user.selectors';
import { ReportingDisaggregatedSelectorMultiLine } from '../ReportingDisaggregatedSelectorMultiLine';

function ReportingMetricsAndKpisSelector({ disaggregatedSelector, hideKpis, labelText, onClickApplyButton }) {
  const intl = useIntl();

  const user = useSelector(getCurrentUser);
  const userRoles = useSelector(getUserRoles);

  const _impressionsKpiChartOptions = useSelector(getImpressionsKpiChartSelector);
  const disaggregatedSelectorPermissions = checkUserResourcePermissions(
    'reportingKPIsGraphsDisaggregatedSelector',
    user,
    userRoles
  );

  // This code snippet removes the options that are not working yet on the endpoint paginated
  const impressionsKpiChartOptions = useMemo(() => {
    if (labelText === 'Events by') {
      return _impressionsKpiChartOptions;
    }
    return {
      kpisOptions: _impressionsKpiChartOptions.kpisOptions.filter(option => {
        return !['visitRetentionRate', 'attn_lumen_attentive_per_mille'].includes(option);
      }),
      metricOptions: _impressionsKpiChartOptions.metricOptions.filter(option => {
        return !['totalSpend'].includes(option);
      })
    };
  }, [_impressionsKpiChartOptions, labelText]);

  const getDefaultOption = selectorOptions => {
    return {
      id: selectorOptions[0].id,
      label: intl.formatMessage({
        id: `reporting.metrics.${selectorOptions[0].id}`
      })
    };
  };

  const getImpressionsKpisChartOptions = selectorOptions => {
    const options = selectorOptions.map(option => {
      return {
        id: getMetricsKeys(option),
        label: intl.formatMessage({
          defaultMessage: getDefaultMessageSelectorKpis(option),
          id: `reporting.metrics.${option}`
        })
      };
    });
    return options;
  };

  const metricOptions = useMemo(() => {
    return getImpressionsKpisChartOptions(impressionsKpiChartOptions.metricOptions);
  }, [impressionsKpiChartOptions.metricOptions]);

  const kpisOptions = useMemo(() => {
    return getImpressionsKpisChartOptions(impressionsKpiChartOptions.kpisOptions);
  }, [impressionsKpiChartOptions.kpisOptions]);

  const [
    { currentMetricsOption, disaggregateByOptionSelected, disaggregateOptionSelected, kpiOption, metricsOption },
    setState
  ] = useState({
    currentMetricsOption: getDefaultOption(metricOptions),
    disaggregateByOptionSelected: null,
    disaggregateOptionSelected: 'none',
    kpiOption: getDefaultOption(kpisOptions),
    metricsOption: getDefaultOption(metricOptions)
  });

  const onChangeDisaggregatedOption = useCallback((option, by) => {
    setState(prevState => ({
      ...prevState,
      disaggregateByOptionSelected: by,
      disaggregateOptionSelected: option
    }));
  }, []);

  const onChangeMetricsSelectorOption = option => {
    setState(prevState => ({
      ...prevState,
      metricsOption: option
    }));
  };

  const onChangeKpisSelectorOption = option => {
    setState(prevState => ({
      ...prevState,
      kpiOption: option
    }));
  };

  const metricsKpisSelected = {
    kpi: kpiOption,
    metric: metricsOption
  };

  const metricsSelected = {
    metric: metricsOption
  };

  const isDisabledApplyButton = () => {
    if (hideKpis && !disaggregatedSelector) {
      return metricsOption.id === currentMetricsOption.id;
    }
    return disaggregateOptionSelected !== 'none' && !disaggregateByOptionSelected;
  };

  const onClickApplyKpisButton = () => {
    setState(prevState => ({
      ...prevState,
      currentMetricsOption: metricsOption
    }));
    onClickApplyButton?.(
      hideKpis ? metricsSelected : metricsKpisSelected,
      disaggregateOptionSelected,
      disaggregateByOptionSelected
    );
  };

  useEffect(() => {
    const result = {
      ...(!hideKpis ? { kpi: getDefaultOption(kpisOptions) } : {}),
      metric: getDefaultOption(metricOptions)
    };
    onClickApplyButton?.(result, disaggregateOptionSelected, disaggregateByOptionSelected);
  }, [impressionsKpiChartOptions, hideKpis]);

  useEffect(() => {
    onClickApplyButton?.(
      hideKpis ? metricsSelected : metricsKpisSelected,
      disaggregateOptionSelected,
      disaggregateByOptionSelected
    );
  }, []);

  const showSecondaryFilter = disaggregatedSelectorPermissions && disaggregatedSelector;

  return (
    <div
      className={cn('metricsAndKpisSelector', {
        'metricsAndKpisSelector--showSecondaryFilter': showSecondaryFilter
      })}
    >
      <div className="metricsAndKpisSelector__filterPrincipal">
        <label className="metricsAndKpisSelector__filterPrincipalLabel fontRegular h3">{labelText}</label>
        <Selector
          className="metricsAndKpisSelector__filterSelector"
          defaultValue={[metricOptions[0]]}
          onChange={onChangeMetricsSelectorOption}
          options={metricOptions}
          type="simple"
        />
        {!hideKpis && (
          <>
            <label className="metricsAndKpisSelector__filterPrincipalLabel fontRegular h3">&</label>
            <Selector
              className="metricsAndKpisSelector__filterSelector"
              defaultValue={[getDefaultOption(kpisOptions)]}
              onChange={onChangeKpisSelectorOption}
              options={kpisOptions}
              type="simple"
            />
          </>
        )}
      </div>
      {showSecondaryFilter && (
        <div className="metricsAndKpisSelector__filterSecondary">
          <ReportingDisaggregatedSelectorMultiLine
            hideKpis={hideKpis}
            metricsKpis={{
              kpi: kpiOption,
              metric: metricsOption
            }}
            onChangeDisaggregatedOption={onChangeDisaggregatedOption}
          />
        </div>
      )}
      <div className="metricsAndKpisSelector__filterApply">
        <ButtonUI
          className="metricsAndKpisSelector__filterButton"
          disabled={isDisabledApplyButton()}
          onClick={onClickApplyKpisButton}
          shape="squareRounded"
          size="medium"
          text={intl.formatMessage({
            defaultMessage: 'Apply',
            id: 'reporting.impressionsKpisChart.selectorButton'
          })}
          type="secondary"
          visible
        />
      </div>
    </div>
  );
}

ReportingMetricsAndKpisSelector.propTypes = {
  disaggregatedSelector: PropTypes.bool,
  hideKpis: PropTypes.bool,
  labelText: PropTypes.string,
  onClickApplyButton: PropTypes.func
};

export default ReportingMetricsAndKpisSelector;
